import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../lib/logger';
import { mealPlanAPI, recipesAPI } from '../lib/api';
import { Navbar } from '../components/layout/Navbar';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Calendar, ChevronLeft, ChevronRight, ShoppingCart,
  Check, Loader2,
} from 'lucide-react';
import { MealGrid } from '../components/mealplan/MealGrid';
import { RecipePickerDialog } from '../components/mealplan/RecipePickerDialog';
import { ShoppingListDialog } from '../components/mealplan/ShoppingListDialog';
import { getWeekDates, formatDate } from '../components/mealplan/constants';

export default function MealPlanPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    today.setDate(diff);
    return today.toISOString().split('T')[0];
  });

  const [mealPlan, setMealPlan] = useState(null);
  const [meals, setMeals] = useState({});
  const [recipes, setRecipes] = useState([]);
  const [showRecipeDialog, setShowRecipeDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showShoppingList, setShowShoppingList] = useState(false);
  const [shoppingList, setShoppingList] = useState(null);
  const [shoppingLoading, setShoppingLoading] = useState(false);

  const weekDates = getWeekDates(currentWeekStart);

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      toast.error('Connectez-vous pour accéder à la planification');
      navigate('/login', { state: { from: { pathname: '/meal-plan' } } });
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading, navigate, currentWeekStart]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [plansRes, recipesRes] = await Promise.all([
        mealPlanAPI.getAll(),
        recipesAPI.getAll({ limit: 100 }),
      ]);

      setRecipes(recipesRes.data);

      const existingPlan = plansRes.data.find((p) => p.start_date === currentWeekStart);
      if (existingPlan) {
        setMealPlan(existingPlan);
        const mealsMap = {};
        existingPlan.meals.forEach((m) => {
          mealsMap[`${m.date}_${m.meal_type}`] = m.recipe_id;
        });
        setMeals(mealsMap);
      } else {
        setMealPlan(null);
        setMeals({});
      }
    } catch (error) {
      logger.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    const current = new Date(currentWeekStart);
    current.setDate(current.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeekStart(current.toISOString().split('T')[0]);
  };

  const openRecipeDialog = (date, mealType) => {
    setSelectedSlot({ date, mealType });
    setShowRecipeDialog(true);
  };

  const selectRecipe = (recipeId) => {
    if (!selectedSlot) return;
    const key = `${selectedSlot.date}_${selectedSlot.mealType}`;
    setMeals((prev) => ({ ...prev, [key]: recipeId }));
    setShowRecipeDialog(false);
    setSelectedSlot(null);
  };

  const removeRecipe = (date, mealType) => {
    const key = `${date}_${mealType}`;
    setMeals((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  const saveMealPlan = async () => {
    setSaving(true);
    try {
      const mealsArray = Object.entries(meals).map(([key, recipeId]) => {
        const [date, mealType] = key.split('_');
        return { date, meal_type: mealType, recipe_id: recipeId };
      });

      if (mealPlan) {
        await mealPlanAPI.update(mealPlan.id, { meals: mealsArray });
        toast.success('Plan de repas mis à jour !');
      } else {
        const weekLabel = `Semaine du ${formatDate(currentWeekStart)}`;
        const response = await mealPlanAPI.create({
          name: weekLabel,
          start_date: currentWeekStart,
          meals: mealsArray,
        });
        setMealPlan(response.data);
        toast.success('Plan de repas créé !');
      }
    } catch (error) {
      logger.error('Failed to save:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const generateShoppingList = async () => {
    if (!mealPlan) {
      toast.error("Sauvegardez d'abord votre plan de repas");
      return;
    }
    setShoppingLoading(true);
    try {
      const response = await mealPlanAPI.getShoppingList(mealPlan.id);
      setShoppingList(response.data);
      setShowShoppingList(true);
    } catch (error) {
      logger.error('Failed to generate shopping list:', error);
      toast.error('Erreur lors de la génération');
    } finally {
      setShoppingLoading(false);
    }
  };

  const toggleShoppingItem = (index) => {
    setShoppingList((prev) => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, checked: !item.checked } : item
      ),
    }));
  };

  const getMealCount = () => Object.keys(meals).length;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-worldplate-cream flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-worldplate-primary" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-worldplate-cream">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8"
        >
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-worldplate-deep">
              Planification des repas
            </h1>
            <p className="text-worldplate-muted mt-1">
              Organisez vos repas de la semaine et générez votre liste de courses
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={generateShoppingList}
              variant="outline"
              disabled={getMealCount() === 0 || shoppingLoading}
              className="gap-2"
              data-testid="generate-shopping-list-btn"
            >
              {shoppingLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ShoppingCart className="w-4 h-4" />
              )}
              Liste de courses
            </Button>
            <Button
              onClick={saveMealPlan}
              disabled={saving}
              className="bg-worldplate-primary hover:bg-worldplate-primary/90 gap-2"
              data-testid="save-meal-plan-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Sauvegarder
            </Button>
          </div>
        </motion.div>

        {/* Week Navigation */}
        <div className="flex items-center justify-between bg-worldplate-paper rounded-2xl p-4 mb-6 shadow-sm border border-worldplate-border">
          <Button
            variant="ghost"
            onClick={() => navigateWeek('prev')}
            className="gap-2"
            data-testid="prev-week-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            Semaine précédente
          </Button>

          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-worldplate-primary" />
            <span className="font-heading text-lg font-semibold text-worldplate-deep">
              Semaine du {formatDate(weekDates[0])} au {formatDate(weekDates[6])}
            </span>
          </div>

          <Button
            variant="ghost"
            onClick={() => navigateWeek('next')}
            className="gap-2"
            data-testid="next-week-btn"
          >
            Semaine suivante
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Meal Grid */}
        {loading ? (
          <div className="grid grid-cols-8 gap-2">
            {[...Array(32)].map((_, i) => (
              <div key={i} className="h-24 bg-worldplate-subtle rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <MealGrid
            weekDates={weekDates}
            meals={meals}
            recipes={recipes}
            onAdd={openRecipeDialog}
            onRemove={removeRecipe}
          />
        )}

        <div className="mt-6 text-center text-worldplate-muted">
          {getMealCount()} repas planifiés cette semaine
        </div>
      </div>

      <RecipePickerDialog
        open={showRecipeDialog}
        onOpenChange={setShowRecipeDialog}
        recipes={recipes}
        onSelect={selectRecipe}
      />

      <ShoppingListDialog
        open={showShoppingList}
        onOpenChange={setShowShoppingList}
        shoppingList={shoppingList}
        onToggleItem={toggleShoppingItem}
      />
    </div>
  );
}
